import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { injectIntl } from 'react-intl';
import { connect, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ActivityTableSection from './ActivityTableSection';
import { AuthProtected, ProcessCoupon } from 'containers';
import { actions } from 'coupon-common/src/modules/dashboards';
import { actions as statsActions } from 'coupon-common/src/modules/stats';
import { getNormalizedEntity } from 'coupon-common/src/utils';
import { actions as followerActions } from 'coupon-common/src/modules/followers/duck';
import { actions as membershipInfoActions } from 'coupon-common/src/modules/memberships/info';
import {
  actions as usedCouponsListActions,
  duck as usedCouponsDuck,
} from 'coupon-common/src/modules/usedCoupons/list';
import { actions as couponsActivityActions } from 'coupon-common/src/modules/usedCoupons/couponsActivity';
import { actions as pointsActivityActions } from 'coupon-common/src/modules/pointsRequests/pointsActivity';
import { actions as pointsTransactionsActions } from 'coupon-common/src/modules/admin/pointsManager/pointsTransactions';
import { actions as couponPointsTransactionActions } from 'coupon-common/src/modules/admin/pointsManager/couponPointsTransactions';
import { actions as pointsBalancesActions } from 'coupon-common/src/modules/admin/pointsManager/pointsBalances';
import { actions as couponsActions, duck as couponsDuck } from 'coupon-common/src/modules/coupons';

import NavBar from './NavBar';
import FeatureBanner from './FeatureBanner';
import BusinessCard from './BusinessCard';
import PremiumCard from './PremiumCard';
import UpgradeToPremiumCard from './UpgradeToPremiumCard';
import ActionsPanel from './ActionsPanel';
import CouponsSidebar from './CouponsSidebar';
import RedemptionsChart from './RedemptionsChart';
import PointsDollarsChart from './PointsDollarsChart';

// Responsive stylesheet (CSS media queries — no JS breakpoint logic needed)
import './dashboard.responsive.css';

// ─── Constants ────────────────────────────────────────────────────────────────
const usedCouponsEntityKey = usedCouponsDuck?.usedCouponsEntity?.key || 'usedCoupons';
const EMPTY_OBJ = {};
const EMPTY_ARR = [];
const DEFAULT_FOLLOWERS = { data: {} };

// The four REDEMPTION_OPTIONS keys backed by the new business-stats
// endpoint, mapped to the field names BusinessStatsView actually returns.
// Anything not in this map (the redemptions $ amount metrics, plus the
// disabled profile_visits/referral_signups options) falls through to the
// existing `dashboards` duck path below.
const STATS_METRIC_MAP = {
  followers: 'followers_total',
  impressions: 'impressions',
  reach: 'unique_impressions',
  clicks: 'clicks',
  favourites: 'favorites',
  redemptions: 'redemptions',
};

// Matches the "Mar – 20 – 2026" em-dash style used by PremiumCard's own
// default value — ported from Account.js so date formatting stays consistent.
const formatRenewalDate = iso => {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  return d
    .toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })
    .replace(/ /g, ' – ');
};

// Approximates a period selection as a day-count back from today, since
// BusinessStatsView only accepts `days` (no start_date/end_date param
// exists on that endpoint yet).
const daysForPeriod = period => {
  if (period.key === 'select_month' && period.startDate) {
    return Math.ceil((Date.now() - new Date(period.startDate)) / 86400000);
  }
  if (period.key === 'date_range' && period.startDate) {
    return Math.ceil((Date.now() - new Date(period.startDate)) / 86400000);
  }
  return 30; // last_30_days, and the fallback for anything unrecognized
};


// Builds the actual date params BusinessStatsView now accepts. Only
// last_30_days has no real start/end from the UI, so that's the only
// case still expressed as `days`.
const statsParamsForPeriod = period => {
  if (period.key === 'select_month' && period.startDate && period.endDate) {
    return { start_date: period.startDate, end_date: period.endDate };
  }
  if (period.key === 'date_range' && period.startDate && period.endDate) {
    return { start_date: period.startDate, end_date: period.endDate };
  }
  return { days: 30 };
};

// ─── Dashboard ────────────────────────────────────────────────────────────────
export const Dashboard = ({
  intl,
  data,
  fetching,
  fetched,
  error,
  dispatch,
  followerState,
  membershipData,
  membershipFetching,
  points,
  referralData,
  referralFetching,
  referralFetched,
  referralError,
  referralInfo,
  usedCouponsListState,
  pointsRequestsData,
  transactionsData,
  transactionsDataFetched,
  couponsData,
  couponsDataFetched,
  balancesData,
  balancesDataFetched,
  currentUserEmail,
  myCoupons,
  viewCounts,
}) => {
  const businessInfo = useSelector(state => state.businessInfo.data);
  const businessId = businessInfo?.id;
  const businessKeyBase = businessId ? String(businessId) : 'all';
  // Two independent slices — one per RedemptionsChart instance — so the
  // two charts fetching the same business's stats concurrently can't
  // clobber each other. See duck.js/saga.js: businessStats is keyed by
  // `${businessId}:${slot}`, not just businessId.
  const businessStatsPrimary = useSelector(
    state => state.stats?.businessStats?.byBusiness?.[`${businessKeyBase}:primary`],
  );
  const businessStatsSecondary = useSelector(
    state => state.stats?.businessStats?.byBusiness?.[`${businessKeyBase}:secondary`],
  );
  const navigate = useNavigate();

  const [activeNav, setActiveNav]           = useState('Dashboard');
  const [usedCouponData, setUsedCouponData] = useState({});

  // ── Local chart state (first RedemptionsChart instance) ─────────────
  const [chartData, setChartData]         = useState(EMPTY_OBJ);
  const [chartFetching, setChartFetching] = useState(false);
  const [chartFetched, setChartFetched]   = useState(false);

  // ── Local chart state (second RedemptionsChart instance) ────────────
  const [chartData2, setChartData2]         = useState(EMPTY_OBJ);
  const [chartFetching2, setChartFetching2] = useState(false);
  const [chartFetched2, setChartFetched2]   = useState(false);


  useEffect(() => {
    if (fetched && data) { setChartData(data); setChartFetched(true); }
    setChartFetching(!!fetching);
  }, [data, fetching, fetched]);

  useEffect(() => {
    if (fetched && data) { setChartData2(data); setChartFetched2(true); }
    setChartFetching2(!!fetching);
  }, [data, fetching, fetched]);

  // Mirrors the primary slot's businessStats slice into chart 1's local
  // state whenever a stats-backed metric (followers/impressions/reach/
  // clicks/favourites) is the active selection — see
  // handleRedemptionFilterChange below.
  useEffect(() => {
    if (!businessStatsPrimary) return;
    setChartFetching(!!businessStatsPrimary.fetching);
    if (businessStatsPrimary.fetched && businessStatsPrimary.data) {
      setChartData(businessStatsPrimary.data); // { window_days, totals, series }
      setChartFetched(true);
    }
  }, [businessStatsPrimary]);

  // Same as above, for chart 2's secondary slot — see
  // handleRedemptionFilterChange2 below.
  useEffect(() => {
    if (!businessStatsSecondary) return;
    setChartFetching2(!!businessStatsSecondary.fetching);
    if (businessStatsSecondary.fetched && businessStatsSecondary.data) {
      setChartData2(businessStatsSecondary.data); // { window_days, totals, series }
      setChartFetched2(true);
    }
  }, [businessStatsSecondary]);

  // ── Data fetching ──────────────────────────────────────────────────
  useEffect(() => {
    dispatch(actions.getData('TOTAL'));
    dispatch(followerActions.getBusinessFollowers());
    dispatch(membershipInfoActions.getMembershipInfo());
    dispatch(usedCouponsListActions.getUsedCoupons());
    dispatch(couponsActions.getBusinessCoupons());
    try {
      dispatch(pointsTransactionsActions.getPointsTransactions());
      dispatch(couponPointsTransactionActions.getCouponTransactions({}));
      dispatch(pointsBalancesActions.getPointsBalances());
    } catch (e) {
      console.warn('Dashboard: Points transaction dispatches failed', e);
    }
  }, [dispatch]);

  useEffect(() => {
    if (businessInfo?.id) {
      dispatch(pointsActivityActions.getPointsRequests({ business: businessInfo.id }));
    }
  }, [dispatch, businessInfo?.id]);

  // Default chart on load: the first RedemptionsChart dropdown defaults to
  // its first REDEMPTION_OPTIONS entry ('redemptions'), which is
  // stats-backed — fire that fetch up front so the chart isn't empty on
  // first paint. slot: 'primary' keeps this in its own reducer slice,
  // independent of the second chart's identical-looking fetch below.
  useEffect(() => {
    if (businessId) {
      dispatch(statsActions.getBusinessStats({ business_id: businessId, days: 30, slot: 'primary' }));
    }
  }, [dispatch, businessId]);

  // Same seed fetch for the second RedemptionsChart instance (defaults to
  // 'followers' via defaultRedemptionKey below), so it isn't empty on
  // first paint either. slot: 'secondary' — see above.
  useEffect(() => {
    if (businessId) {
      dispatch(statsActions.getBusinessStats({ business_id: businessId, days: 30, slot: 'secondary' }));
    }
  }, [dispatch, businessId]);

  // ── Chart filter handlers ───────────────────────────────────────────
  const handleRedemptionFilterChange = useCallback(async ({ type, period }) => {
    if (STATS_METRIC_MAP[type]) {
      // engagement metrics — new business-stats endpoint via the stats duck.
      // businessStatsPrimary's own fetching/fetched flags flow back through
      // the effect above, so no local setChartFetching(...) call is needed
      // here the way the redemptions branch below still needs one.
      if (businessId) {
        dispatch(statsActions.getBusinessStats({
          business_id: businessId,
          slot: 'primary',
          ...statsParamsForPeriod(period),
        }));
      }
      return;
    }

    // unchanged — redemption $ metrics still go through the dashboards duck.
    // NOTE: that duck's action creator only declares a single `range` field
    // and its saga only ever calls api.getDashboardData({ range }) — the
    // `params` object built below (start_date/end_date/period/year/month)
    // is silently dropped before it reaches the backend. Not fixing that
    // as part of this change; flagging so it isn't mistaken for working.
    setChartFetching(true);
    setChartFetched(false);
    try {
      let params = { metric: type };
      if (period.key === 'last_30_days') {
        const end = new Date(), start = new Date();
        start.setDate(start.getDate() - 30);
        params.start_date = start.toISOString().slice(0, 10);
        params.end_date   = end.toISOString().slice(0, 10);
        params.period     = 'last_30_days';
      } else if (period.key === 'select_month') {
        params.start_date = period.startDate;
        params.end_date   = period.endDate;
        params.period     = 'month';
        params.year       = period.year;
        params.month      = period.month;
      } else if (period.key === 'date_range') {
        params.start_date = period.startDate;
        params.end_date   = period.endDate;
        params.period     = 'date_range';
      }
      const result  = await dispatch(actions.getData(type.toUpperCase(), params));
      const newData = result?.data ?? result?.payload ?? result;
      if (newData && typeof newData === 'object') setChartData(newData);
      setChartFetched(true);
    } catch (e) {
      console.warn('RedemptionsChart filter fetch failed:', e);
      setChartFetched(true);
    } finally {
      setChartFetching(false);
    }
  }, [dispatch, businessId]);

  const handleRedemptionFilterChange2 = useCallback(async ({ type, period }) => {
    if (STATS_METRIC_MAP[type]) {
      if (businessId) {
        dispatch(statsActions.getBusinessStats({
          business_id: businessId,
          slot: 'secondary',
          ...statsParamsForPeriod(period),
        }));
      }
      return;
    }

    // unchanged — redemption $ metrics still go through the dashboards duck.
    // Same caveat as above: the params object's date/period fields are
    // silently dropped before reaching the backend on this path.
    setChartFetching2(true);
    setChartFetched2(false);
    try {
      let params = { metric: type };
      if (period.key === 'last_30_days') {
        const end = new Date(), start = new Date();
        start.setDate(start.getDate() - 30);
        params.start_date = start.toISOString().slice(0, 10);
        params.end_date   = end.toISOString().slice(0, 10);
        params.period     = 'last_30_days';
      } else if (period.key === 'select_month') {
        params.start_date = period.startDate;
        params.end_date   = period.endDate;
        params.period     = 'month';
        params.year       = period.year;
        params.month      = period.month;
      } else if (period.key === 'date_range') {
        params.start_date = period.startDate;
        params.end_date   = period.endDate;
        params.period     = 'date_range';
      }
      const result  = await dispatch(actions.getData(type.toUpperCase(), params));
      const newData = result?.data ?? result?.payload ?? result;
      if (newData && typeof newData === 'object') setChartData2(newData);
      setChartFetched2(true);
    } catch (e) {
      console.warn('RedemptionsChart (2) filter fetch failed:', e);
      setChartFetched2(true);
    } finally {
      setChartFetching2(false);
    }
  }, [dispatch, businessId]);

  // ── Derived data ───────────────────────────────────────────────────
  const { entityList: usedCoupons } = useMemo(() => {
    try {
      if (usedCouponsListState && usedCouponsEntityKey) {
        return getNormalizedEntity(usedCouponsListState, usedCouponsEntityKey, intl, {
          id: 'app.usedCouponsEntity',
          defaultMessage: 'used coupons',
        });
      }
    } catch (e) {}
    return { entityList: [], isFetching: false };
  }, [usedCouponsListState, usedCouponsEntityKey]);

  const pointsRequestsList = useMemo(() => {
    const results = Array.isArray(pointsRequestsData)
      ? pointsRequestsData
      : pointsRequestsData?.results;
    if (!results) return [];
    return results.map(req => ({
      id:            req.id,
      first_name:    req.customer?.first_name,
      date_and_time: new Date(req.date_time).toLocaleString(),
      points:        req.amount,
      status:        req.status,
    }));
  }, [pointsRequestsData]);

  const couponsEntityKey = couponsDuck?.couponsEntity?.key || 'coupons';
  const { entityList: couponsList, isFetching: isCouponsFetching } = useMemo(() => {
    try {
      if (myCoupons && couponsEntityKey) {
        return getNormalizedEntity(myCoupons, couponsEntityKey, intl, {
          id: 'app.couponsEntity',
          defaultMessage: 'coupons',
        });
      }
    } catch (e) {}
    return { entityList: [], isFetching: false };
  }, [myCoupons, couponsEntityKey, intl]);

  // ── Handlers ───────────────────────────────────────────────────────
  const handleCouponClick = usedCoupon => {
    setUsedCouponData(usedCoupon);
    dispatch(couponsActivityActions.openModal());
  };

  const handleDeleteCoupon = (id) => {
    dispatch(couponsActions.deleteCoupon(id));
  };

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 18) return 'Good afternoon';
    return 'Good evening';
  };

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <div className="db-page">
      <AuthProtected />
      <div className="db-bg" />

      <NavBar />

      <div className="db-content">

        {/* ── Greeting + Feature Banner ── */}
        <div className="db-header-row">
          <div className="db-greeting">
            <h1>
              {getGreeting()},{' '}
              <strong>{businessInfo?.name || 'there'}</strong>!
            </h1>
            <div className="db-stats">
              {[
                { icon: '🌸', value: followerState?.data?.followers_count || 0, label: 'Total Followers' },
                { icon: '👤', value: referralData?.referrals?.length     || 0, label: 'Total Referrals'  },
              ].map(({ icon, value, label }) => (
                <div key={label} className="db-stat">
                  <span className="db-stat-icon">{icon}</span>
                  <span className="db-stat-value">{value}</span>
                  <span className="db-stat-label">{label}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="db-banner">
            <FeatureBanner />
          </div>
        </div>

        {/* ── Three-column layout ── */}
        <div className="db-main">

          {/* Left sidebar */}
          <div className="db-sidebar-left">
            <BusinessCard businessInfo={businessInfo} data={data} />
            <CouponsSidebar
              couponsList={couponsList}
              isCouponsFetching={isCouponsFetching}
              viewCounts={viewCounts}
              navigate={navigate}
              onDeleteCoupon={handleDeleteCoupon}
            />
          </div>

          {/* Center: Charts + Table */}
          <div className="db-center">
            <div className="db-charts-row">
              <div className="db-chart-wrap">
                <RedemptionsChart
                  data={chartData}
                  fetching={chartFetching}
                  fetched={chartFetched}
                  onFilterChange={handleRedemptionFilterChange}
                />
              </div>
              <div className="db-chart-wrap">
                <RedemptionsChart
                  data={chartData2}
                  fetching={chartFetching2}
                  fetched={chartFetched2}
                  onFilterChange={handleRedemptionFilterChange2}
                  defaultRedemptionKey="followers"
                />
              </div>
              {/* <div className="db-chart-wrap">
                <PointsDollarsChart data={data} fetching={fetching} fetched={fetched} />
              </div> */}
            </div>
            <ActivityTableSection
              pointsRequestsList={pointsRequestsList}
              usedCouponsListState={usedCouponsListState}
              intl={intl}
              onCouponClick={handleCouponClick}
              transactionsData={transactionsData}
              transactionsDataFetched={transactionsDataFetched}
              couponsData={couponsData}
              couponsDataFetched={couponsDataFetched}
              balancesData={balancesData}
              balancesDataFetched={balancesDataFetched}
              currentUserEmail={currentUserEmail}
              referralData={referralData}
              referralFetching={referralFetching}
            />
          </div>

          {/* Right sidebar */}
          <div className="db-sidebar-right">
            {membershipData?.is_free_plan ? (
              <UpgradeToPremiumCard
                onUpgradeClick={() => navigate('/account')}
                code={referralData?.referral_code}
                businessName={businessInfo?.business_name || businessInfo?.name}
              />
            ) : (
              <PremiumCard
  businessName={businessInfo?.business_name || businessInfo?.name}
  points={points}
  code={referralData?.referral_code}
  activeCoupons={
    membershipData?.available_coupons != null && membershipData?.coupon_capacity != null
      ? `${membershipData.available_coupons}/${membershipData.coupon_capacity}`
      : undefined
  }
  renewalDate={formatRenewalDate(membershipData?.renewal_date)}
  autoRenewal={membershipData?.auto_renewal == null ? undefined : (membershipData.auto_renewal ? 'Yes' : 'No')}
/>
            )}
            <ActionsPanel
              usedCoupons={usedCoupons}
              pointsRequestsList={pointsRequestsList}
              onCouponClick={handleCouponClick}
            />
          </div>

        </div>
      </div>

      <ProcessCoupon usedCouponData={usedCouponData} />
    </div>
  );
};

Dashboard.propTypes = {
  data:                    PropTypes.object,
  fetching:                PropTypes.bool,
  fetched:                 PropTypes.bool,
  intl:                    PropTypes.object,
  dispatch:                PropTypes.func,
  followerState:           PropTypes.object,
  membershipData:          PropTypes.object,
  membershipFetching:      PropTypes.bool,
  points:                  PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  referralData:            PropTypes.object,
  referralFetching:        PropTypes.bool,
  referralFetched:         PropTypes.bool,
  referralError:           PropTypes.string,
  referralInfo:            PropTypes.object,
  usedCouponsListState:    PropTypes.object,
  pointsRequestsData:      PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  transactionsData:        PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  transactionsDataFetched: PropTypes.bool,
  couponsData:             PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  couponsDataFetched:      PropTypes.bool,
  balancesData:            PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  balancesDataFetched:     PropTypes.bool,
  currentUserEmail:        PropTypes.string,
  myCoupons:               PropTypes.object,
  viewCounts:              PropTypes.object,
};

const mapStateToProps = state => ({
  ...state.dashboards,
  followerState:           state.followers || DEFAULT_FOLLOWERS,
  membershipData:          state.memberships?.info?.data?.current_plan || EMPTY_OBJ,
  membershipFetching:      !!(state.memberships?.info?.fetching),
  points:                  state.pointsBalance?.data?.points,
  referralData:            state.businessReferralProgram?.data || EMPTY_OBJ,
  referralFetching:        !!(state.businessReferralProgram?.fetching),
  referralFetched:         !!(state.businessReferralProgram?.fetched),
  referralError:           state.businessReferralProgram?.error || null,
  referralInfo:            state.memberships?.referralInfo || EMPTY_OBJ,
  usedCouponsListState:    state.usedCoupons?.list || EMPTY_OBJ,
  pointsRequestsData:      state.pointsRequests?.pointsActivity?.data || EMPTY_OBJ,
  transactionsData:        state.admin?.pointsManager?.pointsTransactions?.data || EMPTY_ARR,
  transactionsDataFetched: !!(state.admin?.pointsManager?.pointsTransactions?.fetched),
  couponsData:             state.admin?.pointsManager?.couponPointsTransactions?.data || EMPTY_ARR,
  couponsDataFetched:      !!(state.admin?.pointsManager?.couponPointsTransactions?.fetched),
  balancesData:            state.admin?.pointsManager?.pointsBalances?.data || EMPTY_ARR,
  balancesDataFetched:     !!(state.admin?.pointsManager?.pointsBalances?.fetched),
  currentUserEmail:        state.businessInfo?.data?.email || '',
  myCoupons:               state.coupons || EMPTY_OBJ,
  viewCounts:              state.coupons?.viewCounts || EMPTY_OBJ,
});

export default connect(mapStateToProps)(injectIntl(Dashboard));